/**
 * Transfer modal — picks a target user to transfer the current call to.
 *
 * Default behavior:
 *   - Loads tenant users from /api/voice/transfer-targets on open
 *   - Sortable columns; auto-faceted on presence + role (per Tom's standing rules)
 *   - "Show offline users" toggle defaults to CHECKED (offline visible, transfer
 *     button disabled on offline rows)
 *   - Two transfer modes per row:
 *       Primary "Transfer" button   → warm handoff (default)
 *       Dropdown chevron            → "Cold transfer" menu item
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Phone, X, ChevronDown } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { formatE164ForDisplay } from "../lib/phone";

export type TransferTarget = {
  id: string;
  email: string;
  displayName: string | null;
  extension: string | null;
  directDialNumber: string | null;
  status: string;
  pbxEnabled: boolean;
  acdEnabled: boolean;
  presence: "online" | "offline";
  isSelf: boolean;
};

type SortKey = "name" | "extension" | "presence";

export function TransferModal({
  callSid,
  onClose,
  onTransferred,
}: {
  callSid: string;
  onClose: () => void;
  onTransferred: () => void;
}) {
  const [items, setItems] = useState<TransferTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showOffline, setShowOffline] = useState(true); // default per Tom's spec
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [transferring, setTransferring] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api<{ items: TransferTarget[] }>("/api/voice/transfer-targets");
      setItems(res.items.filter((u) => !u.isSelf));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((u) => {
      if (!showOffline && u.presence === "offline") return false;
      if (term) {
        const hay = [u.email, u.displayName, u.extension, u.directDialNumber]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [items, search, showOffline]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (sortKey === "name") {
        const aName = a.displayName ?? a.email;
        const bName = b.displayName ?? b.email;
        return aName.localeCompare(bName);
      }
      if (sortKey === "extension") {
        return (a.extension ?? "").localeCompare(b.extension ?? "");
      }
      // presence sorted online-first
      if (a.presence !== b.presence) return a.presence === "online" ? -1 : 1;
      return (a.displayName ?? a.email).localeCompare(b.displayName ?? b.email);
    });
    return arr;
  }, [filtered, sortKey]);

  const transfer = async (targetUserId: string, mode: "warm" | "cold") => {
    setTransferring(targetUserId);
    setMenuOpen(null);
    try {
      await api("/api/voice/transfer", {
        method: "POST",
        body: JSON.stringify({ callSid, targetUserId, mode }),
      });
      onTransferred();
    } catch (e) {
      const msg = e instanceof ApiError
        ? `${e.status}: ${(e.body as { error?: string; message?: string })?.message ?? (e.body as { error?: string })?.error ?? e.message}`
        : (e as Error).message;
      setError(msg);
    } finally {
      setTransferring(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center pad-fluid"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-[min(90vw,56rem)] max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between pad-fluid border-b border-rogue-200">
          <div>
            <h2 className="text-xl font-bold text-rogue-900">Transfer call</h2>
            <div className="text-xs text-rogue-500 mt-1">
              Warm transfer by default. Click the chevron for cold transfer.
            </div>
          </div>
          <button onClick={onClose} className="text-rogue-500 hover:text-rogue-900 p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="pad-fluid border-b border-rogue-200 flex items-center gap-4 flex-wrap">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, extension, DID"
            className="flex-1 min-w-[12rem] border border-rogue-200 rounded px-3 py-2 text-sm outline-none focus:border-rogue-700"
          />
          <label className="flex items-center gap-2 text-sm text-rogue-700">
            <input
              type="checkbox"
              checked={showOffline}
              onChange={(e) => setShowOffline(e.target.checked)}
            />
            Show offline users
          </label>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="border border-rogue-200 rounded px-2 py-1 text-sm"
          >
            <option value="name">Sort: Name</option>
            <option value="extension">Sort: Extension</option>
            <option value="presence">Sort: Presence</option>
          </select>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {error && (
            <div className="m-fluid bg-red-50 text-red-700 rounded-lg pad-fluid text-sm">{error}</div>
          )}
          {loading && (
            <div className="pad-fluid text-rogue-500 text-center">Loading…</div>
          )}
          {!loading && sorted.length === 0 && (
            <div className="pad-fluid text-rogue-500 text-center">No transfer targets match.</div>
          )}
          <table className="w-full text-sm">
            <tbody className="divide-y divide-rogue-100">
              {sorted.map((u) => {
                const offline = u.presence === "offline";
                const disabled = offline || u.status !== "active";
                return (
                  <tr key={u.id} className={offline ? "opacity-60" : ""}>
                    <td className="pad-fluid w-2">
                      <span
                        className={[
                          "inline-block w-2.5 h-2.5 rounded-full",
                          offline ? "bg-rogue-300" : "bg-emerald-500",
                        ].join(" ")}
                        title={u.presence}
                      />
                    </td>
                    <td className="pad-fluid">
                      <div className="font-medium text-rogue-900">{u.displayName || u.email}</div>
                      <div className="text-xs text-rogue-500">{u.email}</div>
                    </td>
                    <td className="pad-fluid font-mono text-rogue-700">
                      {u.extension ? `x${u.extension}` : "—"}
                    </td>
                    <td className="pad-fluid font-mono text-rogue-700 text-xs">
                      {u.directDialNumber ? formatE164ForDisplay(u.directDialNumber) : "—"}
                    </td>
                    <td className="pad-fluid text-xs text-rogue-500">
                      {u.pbxEnabled && <span className="px-2 py-0.5 mr-1 rounded-full bg-blue-50 text-blue-700">PBX</span>}
                      {u.acdEnabled && <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">ACD</span>}
                    </td>
                    <td className="pad-fluid text-right">
                      <div className="inline-flex items-center">
                        <button
                          onClick={() => void transfer(u.id, "warm")}
                          disabled={disabled || transferring === u.id}
                          className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-rogue-200 disabled:text-rogue-500 text-white rounded-l-lg px-3 py-1.5 text-xs flex items-center gap-1"
                        >
                          <Phone className="w-3 h-3" /> Transfer
                        </button>
                        <button
                          onClick={() => setMenuOpen(menuOpen === u.id ? null : u.id)}
                          disabled={disabled}
                          className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-rogue-200 border-l border-emerald-700 disabled:border-rogue-300 text-white rounded-r-lg px-2 py-1.5 text-xs"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                        {menuOpen === u.id && !disabled && (
                          <div
                            className="absolute mt-8 -ml-32 bg-white border border-rogue-200 rounded-lg shadow-lg z-10 text-xs"
                            onMouseLeave={() => setMenuOpen(null)}
                          >
                            <button
                              onClick={() => void transfer(u.id, "cold")}
                              className="block w-32 text-left px-3 py-2 hover:bg-rogue-50 text-rogue-900"
                            >
                              Cold transfer
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
